/**
 * Admin authentication context.
 *
 * - Stores the JWT in localStorage under `le11_admin_token`.
 * - On mount, calls `/api/admin/auth/me` to validate the token.
 * - Exposes `login`, `logout`, `changePassword`, and current `admin` object.
 *
 * NOTE: this only protects the admin UI. The real protection is the backend
 * Bearer token verification on every admin-auth endpoint.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "@/lib/api";

const STORAGE_KEY = "le11_admin_token";
const AdminAuthContext = createContext(null);

const readToken = () => {
  try {
    return typeof window !== "undefined"
      ? window.localStorage.getItem(STORAGE_KEY)
      : null;
  } catch {
    return null;
  }
};

const writeToken = (token) => {
  try {
    if (token) {
      window.localStorage.setItem(STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
};

const authHeaders = (token) =>
  token ? { Authorization: `Bearer ${token}` } : {};

export const AdminAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => readToken());
  const [admin, setAdmin] = useState(null);
  const [ready, setReady] = useState(false);

  // Validate token on mount / token change.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!token) {
        if (!cancelled) {
          setAdmin(null);
          setReady(true);
        }
        return;
      }
      try {
        const { data } = await api.get("/admin/auth/me", {
          headers: authHeaders(token),
        });
        if (!cancelled) {
          setAdmin(data);
          setReady(true);
        }
      } catch {
        if (!cancelled) {
          writeToken(null);
          setToken(null);
          setAdmin(null);
          setReady(true);
        }
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/admin/auth/login", { email, password });
    writeToken(data.access_token);
    setToken(data.access_token);
    setAdmin(data.admin);
    return data.admin;
  }, []);

  const logout = useCallback(() => {
    writeToken(null);
    setToken(null);
    setAdmin(null);
  }, []);

  const changePassword = useCallback(
    async (currentPassword, newPassword) => {
      const { data } = await api.post(
        "/admin/auth/change-password",
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        { headers: authHeaders(token) }
      );
      return data;
    },
    [token]
  );

  const value = useMemo(
    () => ({ token, admin, ready, login, logout, changePassword }),
    [token, admin, ready, login, logout, changePassword]
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth() must be used inside <AdminAuthProvider>");
  }
  return ctx;
};

/** Convenience helper for admin pages doing direct API calls. */
export const useAdminAxios = () => {
  const { token } = useAdminAuth();
  return useMemo(
    () => ({
      get: (url, config = {}) =>
        api.get(url, { ...config, headers: { ...authHeaders(token), ...(config.headers || {}) } }),
      post: (url, body, config = {}) =>
        api.post(url, body, { ...config, headers: { ...authHeaders(token), ...(config.headers || {}) } }),
    }),
    [token]
  );
};
